import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import {
  INFLATABLE_SECTION,
  INFLATABLE_PACKAGES,
  INFLATABLE_PURCHASE_OPTIONS,
  INFLATABLE_LOGO_ACCEPT,
  INFLATABLE_LOGO_HINT,
  INFLATABLE_EIN_HINT,
  INFLATABLE_SMS_TRANSACTIONAL,
  INFLATABLE_SMS_MARKETING,
  PURCHASE_AGREEMENT_PATH,
  formatGuidePrice,
} from '../../constants/guides';
import {
  submitInflatableInquiry,
  fileToDataUrl,
} from '../../services/inflatableInquiry';

const GOLD = '#C9A84C';
const CARD_BG = '#141414';
const INPUT_BG = '#121212';
const TEXT = '#F5F0E8';
const MUTED = 'rgba(245, 240, 232, 0.68)';

const labelStyle = {
  display: 'block',
  fontSize: '0.6875rem',
  fontWeight: 800,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: MUTED,
  marginBottom: '0.4rem',
};

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  backgroundColor: INPUT_BG,
  color: TEXT,
  border: '1px solid #4b5563',
  borderRadius: '0.75rem',
  padding: '0.8rem 0.95rem',
  fontSize: '0.95rem',
  outline: 'none',
  fontFamily: 'inherit',
};

const emptyForm = () => ({
  packageId: 'inflatable-guides',
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  streetAddress: '',
  city: '',
  state: '',
  country: '',
  postalCode: '',
  businessName: '',
  ein: '',
  purchaseOption: '',
  agreePurchaseAgreement: false,
  consentTransactionalSms: false,
  consentMarketingSms: false,
  companyLogoFile: null,
  companyLogoName: '',
});

function Field({ id, label, required, children, hint, fullWidth, isMobile }) {
  return (
    <div style={{ gridColumn: fullWidth && !isMobile ? '1 / -1' : undefined }}>
      <label style={labelStyle} htmlFor={id}>
        {label}
        {required ? ' *' : ''}
      </label>
      {children}
      {hint ? (
        <p style={{ margin: '0.4rem 0 0', color: MUTED, fontSize: '0.8rem', lineHeight: 1.45 }}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export default function InflatableInquirySection({ isMobile }) {
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const setField = (id, value) => {
    setForm((prev) => ({ ...prev, [id]: value }));
  };

  const openInquiry = (packageId) => {
    setField('packageId', packageId);
    setSubmitted(false);
    setError('');
    setShowForm(true);
    requestAnimationFrame(() => {
      document.getElementById('inflatable-purchase-details')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  };

  const onLogoChange = (event) => {
    const file = event.target.files?.[0] || null;
    setForm((prev) => ({
      ...prev,
      companyLogoFile: file,
      companyLogoName: file?.name || '',
    }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!form.purchaseOption) {
      setError('Please select a purchase option.');
      return;
    }
    if (!form.agreePurchaseAgreement) {
      setError('You must agree to the Purchase Agreement.');
      return;
    }
    if (!form.companyLogoFile) {
      setError('Company logo is required to begin production.');
      return;
    }

    setBusy(true);
    try {
      const companyLogoDataUrl = await fileToDataUrl(form.companyLogoFile);
      const { companyLogoFile, ...rest } = form;
      await submitInflatableInquiry({
        ...rest,
        companyLogoDataUrl,
        companyLogoName: form.companyLogoName || companyLogoFile?.name || 'logo',
      });
      setSubmitted(true);
      setForm(emptyForm());
    } catch (err) {
      setError(err?.message || 'Unable to submit inquiry.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      id="inflatable"
      style={{
        padding: isMobile ? '2.5rem 0 3rem' : '3.5rem 0 4.5rem',
        borderTop: '1px solid rgba(201, 168, 76, 0.12)',
        background:
          'linear-gradient(180deg, rgba(201,168,76,0.04) 0%, transparent 28%, transparent 100%)',
        scrollMarginTop: '6rem',
      }}
    >
      <div
        style={{
          maxWidth: '80rem',
          margin: '0 auto',
          padding: '0 clamp(0.75rem, 2vw, 1rem)',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: GOLD,
          }}
        >
          {INFLATABLE_SECTION.eyebrow}
        </p>
        <h2
          style={{
            margin: '0.55rem 0 0',
            fontSize: isMobile ? '1.75rem' : '2.25rem',
            fontWeight: 800,
            color: GOLD,
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
          }}
        >
          {INFLATABLE_SECTION.title}
        </h2>
        <p
          style={{
            margin: '0.85rem 0 0',
            maxWidth: '42rem',
            color: MUTED,
            fontSize: isMobile ? '0.975rem' : '1.05rem',
            lineHeight: 1.65,
          }}
        >
          {INFLATABLE_SECTION.lead}
        </p>

        <div
          style={{
            marginTop: isMobile ? '1.5rem' : '2rem',
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
            gap: isMobile ? '0.9rem' : '1.1rem',
          }}
        >
          {INFLATABLE_PACKAGES.map((pkg) => {
            const selected = showForm && form.packageId === pkg.id;
            return (
              <div
                key={pkg.id}
                style={{
                  textAlign: 'left',
                  backgroundColor: CARD_BG,
                  border: selected
                    ? '1px solid rgba(201, 168, 76, 0.7)'
                    : '1px solid rgba(201, 168, 76, 0.22)',
                  borderRadius: '14px',
                  padding: isMobile ? '1.15rem' : '1.3rem',
                  boxShadow: selected ? '0 10px 28px rgba(0,0,0,0.35)' : 'none',
                  color: TEXT,
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {pkg.badge ? (
                  <span
                    style={{
                      position: 'absolute',
                      top: '0.85rem',
                      right: '0.85rem',
                      padding: '0.28rem 0.5rem',
                      borderRadius: '6px',
                      backgroundColor: GOLD,
                      color: '#0A0A0A',
                      fontSize: '0.625rem',
                      fontWeight: 800,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {pkg.badge}
                  </span>
                ) : null}
                <h3
                  style={{
                    margin: 0,
                    fontSize: isMobile ? '1.15rem' : '1.25rem',
                    fontWeight: 800,
                    paddingRight: pkg.badge ? '6rem' : 0,
                  }}
                >
                  {pkg.title}
                </h3>
                <p style={{ margin: '0.4rem 0 0', color: MUTED, fontSize: '0.9rem', lineHeight: 1.5 }}>
                  {pkg.tagline}
                </p>
                <p
                  style={{
                    margin: '0.85rem 0 0',
                    fontSize: '1.75rem',
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {formatGuidePrice(pkg.priceUsd)}
                </p>
                <ul
                  style={{
                    listStyle: 'none',
                    margin: '0.9rem 0 0',
                    padding: 0,
                    display: 'grid',
                    gap: '0.45rem',
                  }}
                >
                  {pkg.includes.map((item) => (
                    <li
                      key={item}
                      style={{
                        display: 'flex',
                        gap: '0.5rem',
                        alignItems: 'flex-start',
                        fontSize: '0.875rem',
                        color: MUTED,
                      }}
                    >
                      <Check size={15} color={GOLD} style={{ flexShrink: 0, marginTop: '0.15rem' }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => openInquiry(pkg.id)}
                  style={{
                    marginTop: '1.15rem',
                    width: '100%',
                    padding: '0.85rem 1.1rem',
                    borderRadius: '10px',
                    border: `1px solid ${GOLD}`,
                    backgroundColor: selected ? GOLD : 'transparent',
                    color: selected ? '#0A0A0A' : GOLD,
                    fontWeight: 800,
                    fontSize: '0.8125rem',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                  }}
                >
                  {selected ? 'Inquiring' : 'Inquire now'}
                </button>
              </div>
            );
          })}
        </div>

        {showForm ? (
        <div
          id="inflatable-purchase-details"
          style={{
            marginTop: isMobile ? '1.35rem' : '1.75rem',
            backgroundColor: CARD_BG,
            border: '1px solid rgba(201, 168, 76, 0.22)',
            borderRadius: '16px',
            padding: isMobile ? '1.2rem' : '1.5rem',
            scrollMarginTop: '6rem',
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: isMobile ? '1.15rem' : '1.3rem',
              fontWeight: 800,
              color: TEXT,
            }}
          >
            Purchase details
          </h3>
          <p style={{ margin: '0.45rem 0 0', color: MUTED, fontSize: '0.9rem', lineHeight: 1.55 }}>
            Completing inquiry for{' '}
            <span style={{ color: TEXT, fontWeight: 700 }}>
              {INFLATABLE_PACKAGES.find((pkg) => pkg.id === form.packageId)?.title || 'your package'}
            </span>
            . We will follow up to confirm shipping and collect payment. No charge is processed online.
          </p>

          {submitted ? (
            <div
              role="status"
              style={{
                marginTop: '1.25rem',
                padding: '1rem 1.1rem',
                borderRadius: '12px',
                border: '1px solid rgba(201, 168, 76, 0.4)',
                backgroundColor: 'rgba(201, 168, 76, 0.1)',
                color: TEXT,
                lineHeight: 1.55,
              }}
            >
              Inquiry received. We will follow up by email or phone shortly.
            </div>
          ) : (
            <form onSubmit={onSubmit} style={{ marginTop: '1.25rem' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: '0.85rem',
                }}
              >
                <Field id="inflatable-firstName" label="First Name" required isMobile={isMobile}>
                  <input
                    id="inflatable-firstName"
                    type="text"
                    value={form.firstName}
                    onChange={(e) => setField('firstName', e.target.value)}
                    required
                    autoComplete="given-name"
                    placeholder="First Name"
                    style={inputStyle}
                  />
                </Field>
                <Field id="inflatable-lastName" label="Last Name" required isMobile={isMobile}>
                  <input
                    id="inflatable-lastName"
                    type="text"
                    value={form.lastName}
                    onChange={(e) => setField('lastName', e.target.value)}
                    required
                    autoComplete="family-name"
                    placeholder="Last Name"
                    style={inputStyle}
                  />
                </Field>
                <Field id="inflatable-phone" label="Phone" required isMobile={isMobile}>
                  <input
                    id="inflatable-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setField('phone', e.target.value)}
                    required
                    autoComplete="tel"
                    placeholder="Phone"
                    style={inputStyle}
                  />
                </Field>
                <Field id="inflatable-email" label="Email" required isMobile={isMobile}>
                  <input
                    id="inflatable-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setField('email', e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="Email"
                    style={inputStyle}
                  />
                </Field>

                <Field
                  id="inflatable-streetAddress"
                  label="Street Address"
                  fullWidth
                  isMobile={isMobile}
                  hint="Search address or enter your full address"
                >
                  <input
                    id="inflatable-streetAddress"
                    type="text"
                    value={form.streetAddress}
                    onChange={(e) => setField('streetAddress', e.target.value)}
                    autoComplete="street-address"
                    placeholder="Enter your full address"
                    style={inputStyle}
                  />
                </Field>

                <Field id="inflatable-city" label="City" required isMobile={isMobile}>
                  <input
                    id="inflatable-city"
                    type="text"
                    value={form.city}
                    onChange={(e) => setField('city', e.target.value)}
                    required
                    autoComplete="address-level2"
                    placeholder="City"
                    style={inputStyle}
                  />
                </Field>
                <Field id="inflatable-state" label="State" required isMobile={isMobile}>
                  <input
                    id="inflatable-state"
                    type="text"
                    value={form.state}
                    onChange={(e) => setField('state', e.target.value)}
                    required
                    autoComplete="address-level1"
                    placeholder="State"
                    style={inputStyle}
                  />
                </Field>
                <Field id="inflatable-country" label="Country" isMobile={isMobile}>
                  <input
                    id="inflatable-country"
                    type="text"
                    value={form.country}
                    onChange={(e) => setField('country', e.target.value)}
                    autoComplete="country-name"
                    placeholder="Enter your country"
                    style={inputStyle}
                  />
                </Field>
                <Field id="inflatable-postalCode" label="Postal Code" isMobile={isMobile}>
                  <input
                    id="inflatable-postalCode"
                    type="text"
                    value={form.postalCode}
                    onChange={(e) => setField('postalCode', e.target.value)}
                    autoComplete="postal-code"
                    placeholder="ZIP or postal code"
                    style={inputStyle}
                  />
                </Field>

                <Field id="inflatable-businessName" label="Business Name" required isMobile={isMobile}>
                  <input
                    id="inflatable-businessName"
                    type="text"
                    value={form.businessName}
                    onChange={(e) => setField('businessName', e.target.value)}
                    required
                    autoComplete="organization"
                    placeholder="Business Name"
                    style={inputStyle}
                  />
                </Field>
                <Field
                  id="inflatable-ein"
                  label="EIN"
                  required
                  isMobile={isMobile}
                  hint={INFLATABLE_EIN_HINT}
                >
                  <input
                    id="inflatable-ein"
                    type="text"
                    value={form.ein}
                    onChange={(e) => setField('ein', e.target.value)}
                    required
                    placeholder="EIN Number"
                    style={inputStyle}
                  />
                </Field>

                <Field
                  id="inflatable-logo"
                  label="Company Logo"
                  required
                  fullWidth
                  isMobile={isMobile}
                  hint={INFLATABLE_LOGO_HINT}
                >
                  <input
                    id="inflatable-logo"
                    type="file"
                    accept={INFLATABLE_LOGO_ACCEPT}
                    onChange={onLogoChange}
                    required
                    style={{
                      ...inputStyle,
                      padding: '0.65rem 0.85rem',
                      cursor: 'pointer',
                    }}
                  />
                  {form.companyLogoName ? (
                    <p style={{ margin: '0.35rem 0 0', color: GOLD, fontSize: '0.8rem' }}>
                      Selected: {form.companyLogoName}
                    </p>
                  ) : null}
                </Field>
              </div>

              <fieldset
                style={{
                  margin: '1.15rem 0 0',
                  padding: 0,
                  border: 'none',
                }}
              >
                <legend style={{ ...labelStyle, marginBottom: '0.65rem' }}>
                  Purchase Option *
                </legend>
                <div style={{ display: 'grid', gap: '0.55rem' }}>
                  {INFLATABLE_PURCHASE_OPTIONS.map((option) => {
                    const selected = form.purchaseOption === option.id;
                    return (
                      <label
                        key={option.id}
                        style={{
                          display: 'flex',
                          gap: '0.7rem',
                          alignItems: 'flex-start',
                          padding: '0.85rem 0.95rem',
                          borderRadius: '0.75rem',
                          border: selected
                            ? '1px solid rgba(201, 168, 76, 0.7)'
                            : '1px solid #4b5563',
                          backgroundColor: INPUT_BG,
                          cursor: 'pointer',
                          color: TEXT,
                        }}
                      >
                        <input
                          type="radio"
                          name="purchaseOption"
                          value={option.id}
                          checked={selected}
                          onChange={() => setField('purchaseOption', option.id)}
                          required
                          style={{ marginTop: '0.2rem' }}
                        />
                        <span style={{ fontSize: '0.95rem', lineHeight: 1.45 }}>
                          {option.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>

              <div style={{ marginTop: '1.15rem', display: 'grid', gap: '0.85rem' }}>
                <label
                  style={{
                    display: 'flex',
                    gap: '0.7rem',
                    alignItems: 'flex-start',
                    color: TEXT,
                    fontSize: '0.9rem',
                    lineHeight: 1.55,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.agreePurchaseAgreement}
                    onChange={(e) => setField('agreePurchaseAgreement', e.target.checked)}
                    required
                    style={{ marginTop: '0.25rem' }}
                  />
                  <span>
                    Purchase Agreement * I have read, understand, and agree to the{' '}
                    <Link
                      to={PURCHASE_AGREEMENT_PATH}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: GOLD }}
                    >
                      Purchase Agreement
                    </Link>
                    .
                  </span>
                </label>

                <label
                  style={{
                    display: 'flex',
                    gap: '0.7rem',
                    alignItems: 'flex-start',
                    color: MUTED,
                    fontSize: '0.85rem',
                    lineHeight: 1.55,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.consentTransactionalSms}
                    onChange={(e) => setField('consentTransactionalSms', e.target.checked)}
                    style={{ marginTop: '0.25rem' }}
                  />
                  <span>{INFLATABLE_SMS_TRANSACTIONAL}</span>
                </label>

                <label
                  style={{
                    display: 'flex',
                    gap: '0.7rem',
                    alignItems: 'flex-start',
                    color: MUTED,
                    fontSize: '0.85rem',
                    lineHeight: 1.55,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.consentMarketingSms}
                    onChange={(e) => setField('consentMarketingSms', e.target.checked)}
                    style={{ marginTop: '0.25rem' }}
                  />
                  <span>{INFLATABLE_SMS_MARKETING}</span>
                </label>
              </div>

              {error ? (
                <p role="alert" style={{ margin: '0.9rem 0 0', color: '#fca5a5', fontSize: '0.875rem' }}>
                  {error}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={busy}
                style={{
                  marginTop: '1.15rem',
                  width: isMobile ? '100%' : 'auto',
                  minWidth: '14rem',
                  padding: '0.95rem 1.4rem',
                  borderRadius: '10px',
                  border: `1px solid ${GOLD}`,
                  backgroundColor: GOLD,
                  color: '#0A0A0A',
                  fontWeight: 800,
                  fontSize: '0.875rem',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  cursor: busy ? 'wait' : 'pointer',
                  opacity: busy ? 0.8 : 1,
                }}
              >
                {busy ? 'Sending…' : 'Submit purchase inquiry'}
              </button>
            </form>
          )}
        </div>
        ) : null}
      </div>
    </section>
  );
}
